/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * @file: SiteInspectionManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { InspectionSiteInfo } from '@euler/model';
import {
  AbnormalLevel,
  CommonTaskStatus,
  InspectionTemplateSceneType,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import {
  Inspection,
  SiteInspection,
  SiteInspectionCommitPayload,
  SiteInspectionItemStaged,
  SiteInspectionStaged,
  TaskDetail,
  TaskMediaStaged,
  VersionedResponse,
} from '@euler/model/task-detail';
import { RelatedFaultLikeSubject } from '@euler/model/task-detail/RelatedFaultLikeSubject';
import { array2map } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import produce, { Immutable, freeze } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { nanoid } from 'nanoid/non-secure';
import { BehaviorSubject } from 'rxjs';
import {
  MediaUploadTask,
  MediaUploadTaskEvent,
} from '../MediaFileUploadController';
import { IInspectionManager, TaskFileUploadRequestContext } from '../types';
import {
  siteInspectionStagedToCommitPayload,
  siteInspectionToStaged,
} from '../util';
import { InspectionManagerBase } from './InspectionManagerBase';

export type SiteInspectionParams = {
  flowId: string;
  site: InspectionSiteInfo;
  inspectionType: SiteInspectionType;
  technicianId: number;
  technicianName: string;
  bundle?: string;
};

export class SiteInspectionManager<
  T extends Inspection,
> extends InspectionManagerBase<T> {
  // represents staged commit payload for site inspection
  readonly staged$: BehaviorSubject<SiteInspectionStaged>;
  readonly removeFileUploadListener: () => void;
  readonly dirty$: BehaviorSubject<boolean>;
  readonly id = nanoid();
  private _isCommitting = false;

  constructor(
    private readonly inspectionManager: IInspectionManager<T>,
    readonly params: Readonly<SiteInspectionParams>,
  ) {
    super(inspectionManager.category, inspectionManager.taskManager);

    // initialize the staged commit payload for this site.
    const inspection = this.getTargetInspection(this.cachedDetail);
    const siteInspection = inspection.sites.find(
      x => x.siteId === this.params.site.id,
    );

    // convert the site inspection to commit payload
    const staged: SiteInspectionStaged = this.toStaged(siteInspection);

    this.staged$ = new BehaviorSubject(freeze(staged));
    this.dirty$ = new BehaviorSubject<boolean>(false);

    this.removeFileUploadListener =
      this.taskManager.uploadController.addListener((e, task) => {
        this.onFileUploadEvent(e, task as any);
      });
  }

  get isCommitting() {
    return this._isCommitting;
  }

  reset() {
    // initialize the staged commit payload for this site.
    const inspection = this.getTargetInspection(this.cachedDetail);
    const siteInspection = inspection.sites.find(
      x => x.siteId === this.params.site.id,
    );

    // convert the site inspection to commit payload
    const staged: SiteInspectionStaged = this.toStaged(siteInspection);
    this.staged$.next(staged);
    this.markAsDirty(false);
  }

  dispose() {
    this.removeFileUploadListener();
  }

  removeMedia(itemKey: string, key: string) {
    this.taskManager.uploadController.abort(key);
    this.produceItem(itemKey, (item, original, site) => {
      const k = original.medias.findIndex(x => x._key === key);
      if (k < 0) {
        console.error(new Error('Cannot find target media, this must be bug'));
        return;
      }
      item.medias.splice(k, 1);
      // reset the facade item's defective level to fine where all medias have been deleted.
      if (
        site.inspectionType === SiteInspectionType.Facade &&
        item.medias.length === 0
      ) {
        item.abnormalLevel = AbnormalLevel.Fine;
        item.severityLevel = SeverityLevel.None;
      }
    });
  }

  findMedia(itemKey: string, key: string) {
    const current = this.staged$.getValue();
    const item = current.items.find(x => x._key === itemKey);
    if (!item) return undefined;
    return item.medias.find(x => x._key === key);
  }

  /**
   * Commit the inspected results.
   */
  async commit() {
    let cancel = false;
    this._isCommitting = true;
    try {
      const payload = this.getCommitPayload();
      if (
        payload.inspectionType === SiteInspectionType.Facade &&
        payload.items.every(x => !x.medias.length)
      ) {
        // cancel the inspection.
        cancel = true;
        await this.inspectionManager.cancelSiteInspection(payload.siteId);
        return;
      }
      this.inspectionManager.siteInspectionWillCommit(this.params.site.id);
      const res = await this.taskService.commitSiteInspection(
        this.taskNo,
        payload,
      );
      this.handleVersionedResponse(
        res,
        (_, { inspection, original, response }) => {
          this.beginApplyCommitResult(response, inspection, original, res);
        },
      );
      this.endApplyCommitResult(res.response);
    } finally {
      this._isCommitting = false;
      if (!cancel) {
        this.inspectionManager.siteInspectionDidCommit(this.params.site.id);
      }
    }
  }

  enumerate(callbacks: {
    onItem?: (item: SiteInspectionItemStaged) => boolean | undefined;
    onMedia?: (
      media: TaskMediaStaged,
      item: SiteInspectionItemStaged,
    ) => boolean | undefined;
  }) {
    const staged = this.staged$.getValue();
    for (const item of staged.items) {
      if (false === callbacks.onItem?.(item)) {
        break;
      }
      let skip = false;
      for (const media of item.medias) {
        if (false === callbacks.onMedia?.(media, item)) {
          skip = true;
          break;
        }
      }
      if (skip) break;
    }
  }

  beginApplyCommitResult(
    response: SiteInspection,
    inspection: T,
    original: T,
    res: VersionedResponse<SiteInspection>,
  ) {
    const index = original.sites.findIndex(
      x => x.siteId === this.params.site.id,
    );
    if (index < 0) {
      inspection.sites.push(response);
    } else {
      inspection.sites[index] = response;
    }
    if (inspection.status !== CommonTaskStatus.InProgress) {
      inspection.status = CommonTaskStatus.InProgress;
    }
    const inspectionType = response.inspectionType;
    if (
      inspectionType === SiteInspectionType.Dashboard ||
      inspectionType === SiteInspectionType.Facade
    ) {
      const sceneType =
        inspectionType === SiteInspectionType.Facade
          ? InspectionTemplateSceneType.Facade
          : InspectionTemplateSceneType.Dashboard;
      const k = original.flows.findIndex(
        x => x.template.sceneType === sceneType,
      );
      const flow = inspection.flows[k];
      if (flow && flow.status === 'pending') {
        flow.status = 'inprogress';
        flow.updatedAt = dateFromValue(res.timestamp);
      }
    }
  }

  endApplyCommitResult(response: SiteInspection) {
    // save current staged fault like subjects state.
    const map = array2map(
      this.staged$.getValue().items,
      x => (x.type === 'item' ? x.itemId : x.inspectionResult),
      x => x.relatedFaultLikeSubjects,
    );
    const nextStaged = this.toStaged(response, undefined, map);
    this.staged$.next(nextStaged);
    this.markAsDirty(false);
  }

  getCommitPayload(): SiteInspectionCommitPayload {
    // prepare commit payloads by converting staged information
    const staged = this.staged$.getValue();
    return siteInspectionStagedToCommitPayload(staged, {
      flowId: this.params.flowId,
      inspectionType: this.params.inspectionType,
      siteId: this.params.site.id,
      siteName: this.params.site.name,
      technicianId: this.params.technicianId,
      technicianName: this.params.technicianName,
    });
  }

  toStaged(
    siteInspection: SiteInspection | undefined,
    detail?: TaskDetail,
    relatedFaultLikeSubjectMap?: Map<
      string | number,
      RelatedFaultLikeSubject[] | undefined
    >,
  ) {
    detail = detail ?? this.cachedDetail;
    const staged = siteInspectionToStaged(
      this.params.flowId,
      this.params.site,
      this.params.inspectionType,
      siteInspection,
      this.params.technicianId,
      this.params.technicianName,
    );
    staged.bundle = this.params.bundle;
    staged.items.forEach(inspectedItem => {
      inspectedItem.relatedFaultLikeSubjects =
        relatedFaultLikeSubjectMap != null
          ? relatedFaultLikeSubjectMap.get(
              inspectedItem.type === 'item'
                ? inspectedItem.itemId
                : inspectedItem.inspectionResult,
            )
          : detail!.basicInfo.orderDetail?.serviceSubjects
              ?.filter(isFaultLikeServiceSubject)
              ?.map(subject => ({
                name: subject.name,
                checked: subject.relatedInspections?.some(
                  x =>
                    (x.type === 'item' &&
                      inspectedItem.type === 'item' &&
                      x.siteCode === this.params.site.code &&
                      x.itemName === inspectedItem.name) ||
                    (x.type === 'custom-issue' &&
                      inspectedItem.type === 'customIssue' &&
                      x.siteCode === this.params.site.code &&
                      (x.itemName ?? '') === (inspectedItem.itemName ?? '')),
                ),
              }));
    });
    return staged;
  }

  produce(
    recipe: (
      draft: WritableDraft<SiteInspectionStaged>,
      original: SiteInspectionStaged,
    ) => void,
  ) {
    const current = freeze(this.staged$.getValue());
    const nextState = produce(current, draft => {
      recipe(draft, current);
    });
    if (nextState !== current) {
      this.staged$.next(nextState);
      this.markAsDirty(true);
    }
  }

  produceItem(
    key: string,
    recipe: (
      draft: WritableDraft<SiteInspectionItemStaged>,
      original: SiteInspectionItemStaged,
      originalState: SiteInspectionStaged,
      index: number,
    ) => void,
    options?: {
      debugLabel?: string;
      ignoreKeyError?: boolean;
    },
  ) {
    const current = this.staged$.getValue();
    const index = current.items.findIndex(x => x._key === key);
    if (index < 0) {
      if (!options?.ignoreKeyError) {
        const detail = `current items: ${JSON.stringify(current.items)}`;
        let debugLabel = options?.debugLabel;
        if (debugLabel) debugLabel = `[${debugLabel}] `;
        console.error(
          new Error(
            `${
              debugLabel ?? ''
            }Cannot find check item with key '${key}', this must be bug. detail: ${detail}`,
          ),
        );
      }
      return;
    } else {
      this.produceItemAt(index, recipe);
    }
  }

  produceItemAt(
    index: number,
    recipe: (
      draft: WritableDraft<SiteInspectionItemStaged>,
      original: SiteInspectionItemStaged,
      originalState: SiteInspectionStaged,
      index: number,
    ) => void,
  ) {
    this.produce((draft, original) => {
      const item = draft.items[index];
      recipe(item, original.items[index], original, index);
    });
  }

  private markAsDirty(dirty: boolean) {
    if (this.dirty$.getValue() !== dirty) {
      this.dirty$.next(dirty);
    }
  }

  private onFileUploadEvent(
    e: MediaUploadTaskEvent,
    task: Immutable<MediaUploadTask<TaskFileUploadRequestContext>>,
  ) {
    const request = task.request;
    const context = request.context;

    // ignore unwanted event that is not specific to me.
    if (
      context == null ||
      context.type !== 'item-inspection' ||
      context.siteInspectionManagerId !== this.id ||
      context.siteId !== this.params.site.id
    ) {
      return;
    }

    if (e.type === 'queued') {
      // add a new media state when queued.
      this.produceItem(context.key, (draft, original) => {
        if (context.mediaKey) {
          const index = original.medias.findIndex(
            x => x._key === context.mediaKey,
          );
          if (index >= 0) {
            const media = draft.medias[index];
            if (context.annotationMetadata != null) {
              media.annotationMetadata = context.annotationMetadata as any;
            }
            media._source = {
              type: 'local',
              localFileUri: request.localFileUri,
              contentType: request.contentType,
              uploadParameters: request.parameters,
              state: { status: 'queued' },
            };
            return;
          }
        }

        draft.medias.push({
          type: request.contentType,
          annotationMetadata: context.annotationMetadata as any,
          _key: context.mediaKey ?? task.key,
          _timestamp: Date.now(),
          _source: {
            type: 'local',
            localFileUri: request.localFileUri,
            contentType: request.contentType,
            uploadParameters: request.parameters,
            state: { status: 'queued' },
          },
        });
      });
    } else {
      // update the existing media state.
      this.produceItem(context.key, (draft, original) => {
        // use original item to speed up searching
        const index = original.medias.findIndex(
          x => x._key === (context.mediaKey ?? task.key),
        );
        if (index < 0) return;
        const media = draft.medias[index];
        if (media._source.type !== 'local') return;
        switch (e.type) {
          case 'compressing': {
            media._source.state = {
              status: 'compressing',
              progress: e.progress,
            };
            return;
          }
          case 'begin': {
            media._source.state = { status: 'uploading', progress: 0 };
            return;
          }
          case 'progress': {
            media._source.state = {
              status: 'uploading',
              progress: e.progress,
            };
            return;
          }
          case 'error': {
            media._source.state = { status: 'error', error: e.error };
            return;
          }
          case 'abort': {
            if (task.status !== 'aborted') {
              media._source.state = {
                status: 'error',
                error: e.error ?? new Error('File uploading has aborted'),
              };
            }
            return;
          }
          case 'finished': {
            const cover = e.result.extra?.cover;
            media.coverUrl = cover != null && cover !== '' ? cover : undefined;
            media._source = {
              type: 'remote',
              url: e.result.url,
              cid: e.result.name,
            };
            return;
          }
          default:
            return;
        }
      });
    }
  }
}
